import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal, Type } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { userDtos } from '@vectronic/openApi-customer';
import {
  CardComponent,
  ConfirmationDialogComponent,
  FilterHeaderComponent,
  FiltersComponent,
} from '@vectronic/ui';
import { DialogService, EventRequestDto } from '@vectronic/util';
import { EXTERNAL_MAP_LAYERS_SERVICE } from '../../services/external-layers/external-map-layers.service';
import { FeatureLayerDetailsService } from '../../services/map/feature-layer-details.service';
import { MapDistanceMeasurementService } from '../../services/map/map-distance-measurement.service';
import { MapService } from '../../services/map/map.service';
import { DrawingManagerService } from '../../services/virtualFence/drawing-manager.service';
import { FeatureLayerDetailsPanelComponent } from './feature-layer-details-panel/feature-layer-details-panel.component';
import { LayersPanelComponent } from './layers-panel/layers-panel.component';

export interface ToolbarButton {
  icon: string;
  label: string;
  panelComponent?: Type<any>;
  panelData?: any;
  disabled?: boolean;
  isFilterActive?: boolean;
  id?: string;
}

export interface CompareCollarOption {
  key: string;
  collarId: number;
  label: string;
  tooltip?: string;
  color: string;
}

@Component({
  selector: 'bm-map-toolbar',
  imports: [
    CommonModule,
    MatIconModule,
    FiltersComponent,
    LayersPanelComponent,
    FeatureLayerDetailsPanelComponent,
    CardComponent,
    MatTooltipModule,
    FilterHeaderComponent,
    MatCheckboxModule,
    MatBadgeModule,
  ],
  templateUrl: './map-toolbar.component.html',
  styleUrls: ['./map-toolbar.component.scss'],
})
export class MapToolbarComponent {
  isDashboardPage = input<boolean>();
  userPreferences = input<userDtos.UserPreferences>();
  filtersToDisplay = input<string[]>();
  pageName = input<string>();
  infoText = input<string>();
  showEval = input<boolean>(false);
  evalActive = input<boolean>(false);
  evalDisabled = input<boolean>(false);
  showGpsPositions = input<boolean>(false);
  showDeadReckoningPath = input<boolean>(false);
  deadReckoningPathActive = input<boolean>(true);
  showSpeedHeatmap = input<boolean>(false);
  speedHeatmapActive = input<boolean>(false);
  showCompareCollars = input<boolean>(false);
  compareOptions = input<CompareCollarOption[]>([]);
  selectedCompareKeys = input<string[]>([]);
  initialLastXRecordsValue = input<number>();
  hideSettingsBtn = input<boolean>();
  hideAutoFocus = input<boolean>();
  autoFocusEnabled = input<boolean>(true);

  isFilterActive = signal<boolean>(false);
  autoFocusEnabledState = signal<boolean>(true);

  isGpsPositionsActive = computed(() => {
    this.mapService.layerChanges();
    const prefs = this.mapService.userPreferences;
    return Boolean(prefs?.showMarkers || prefs?.mapLightConditionIconToggle);
  });

  // virtual fence data
  virtualFenceFilterToggled = signal<boolean>(true);
  isVirtualFenceEnabled = input<boolean>();
  selectedCollarIds = input(null, {
    transform: (value: number[] | null) => {
      if (!value) return null;

      return value.map((id) => ({ id, checked: true }));
    },
  });

  selectedCollarsFilterList = signal<{ id: number; checked: boolean }[]>([]);
  virtualFenceZoomToCollar = output<{ collarId: any }>();
  selectedCollarsFilterListChange = output<{ id: number; checked: boolean }[]>();
  autoFocusToggled = output<boolean>();
  evalToggled = output<boolean>();
  gpsPositionsToggled = output<boolean>();
  deadReckoningPathToggled = output<boolean>();
  speedHeatmapToggled = output<boolean>();
  compareSelectionChanged = output<string[]>();

  private initInfoPanelEffect = effect(() => {
    const infoText = this.infoText();
    const pageName = this.pageName();
    if (infoText && pageName) {
      const storageKey = `${pageName}_info_auto_opened`;
      const alreadyOpened = sessionStorage.getItem(storageKey);
      if (!alreadyOpened) {
        this.openPanelIndex = 0;
        sessionStorage.setItem(storageKey, 'true');
      }
    }
  });

  private syncToolbarStateEffect = effect(() => {
    const autoFocusEnabled = this.autoFocusEnabled();
    if (autoFocusEnabled !== this.autoFocusEnabledState()) {
      this.autoFocusEnabledState.set(autoFocusEnabled);
    }

    const incomingCollarIds = this.selectedCollarIds();
    this.selectedCollarsFilterList.update((currentList) => {
      const newList = [...currentList];
      const incomingMap = new Map<number, { id: number; checked: boolean }>();
      if (incomingCollarIds) {
        incomingCollarIds.forEach((collar) => incomingMap.set(collar.id, collar));
      }

      // Update existing collars and mark unchecked if not in incomingCollarIds
      for (const existingCollar of newList) {
        if (incomingMap.has(existingCollar.id)) {
          existingCollar.checked = true;
          incomingMap.delete(existingCollar.id); // Remove from map to track new collars
        } else {
          existingCollar.checked = false;
        }
      }

      // Add new collars from incomingCollarIds
      incomingMap.forEach((newCollar) => newList.push(newCollar));

      return newList;
    });

    // reset toolbar icon design state
    if (this.mapDistanceMeasurementService.measurementCompleted()) {
      this.openPanelIndex = null;
      this.enableDistanceMeasurement = false;
      this.mapDistanceMeasurementService.measurementCompleted.set(false);
    }

    // Effect to update the disabled state of the distance measurement button.
    // This is needed because 'enableDistanceMeasurement' is not a signal.
    const distanceButton = this.toolbarButtons().find((btn) => btn.icon === 'design_services');
    if (distanceButton) {
      distanceButton.disabled = this.drawingManager.activeDrawingMode() === 'virtual-fence';
    }
  });

  filterValuesSaved = output<EventRequestDto>();

  onCollarIdChange(event: { checked: boolean; collar: { id: number } }): void {
    this.selectedCollarsFilterList.update((currentList) => {
      const collarIndex = currentList.findIndex((item) => item.id === event.collar.id);

      if (collarIndex !== -1) {
        const updatedList = [...currentList];
        updatedList[collarIndex] = { ...updatedList[collarIndex], checked: event.checked };
        this.selectedCollarsFilterListChange.emit(updatedList);
        return updatedList;
      }
      return currentList;
    });
  }

  onCompareOptionToggle(key: string, event: Event) {
    const { checked } = event.target as HTMLInputElement;
    const current = this.selectedCompareKeys();
    let nextKeys;
    if (checked) {
      nextKeys = current.includes(key) ? current : [...current, key];
    } else {
      nextKeys = current.filter((item) => item !== key);
    }
    this.compareSelectionChanged.emit(nextKeys);
  }

  private router = inject(Router);
  private dialogService = inject(DialogService);
  private mapService = inject(MapService);
  private mapDistanceMeasurementService = inject(MapDistanceMeasurementService);
  private drawingManager = inject(DrawingManagerService);
  private externalLayersService = inject(EXTERNAL_MAP_LAYERS_SERVICE, { optional: true });
  protected featureLayerDetails = inject(FeatureLayerDetailsService);

  protected readonly FiltersComponent = FiltersComponent;
  protected readonly LayersPanelComponent = LayersPanelComponent;

  protected layersPanelWidth = computed(() => {
    const service = this.externalLayersService;
    const showProviderToggle = Boolean(service && service?.available?.() !== false);
    return showProviderToggle ? '200px' : '175px';
  });

  private readonly _toolbarButtons: ToolbarButton[] = [
    {
      icon: 'filter_alt',
      label: 'Positions Filters',
      panelComponent: FiltersComponent,
      id: 'map-positions-filters',
    },
    {
      icon: 'layers',
      label: 'Manage Layers',
      panelComponent: LayersPanelComponent,
      id: 'map-layers-panel',
    },
    {
      icon: 'settings',
      label: 'Navigate to Settings',
      id: 'map-toolbar-settings-btn',
    },
    {
      icon: 'design_services',
      label: 'Measure Distance',
      id: 'map-toolbar-measure-distance-btn',
    },
    {
      icon: 'zoom_in',
      label: 'Zoom In',
      id: 'map-toolbar-zoom-in-btn',
    },
    {
      icon: 'zoom_out',
      label: 'Zoom Out',
      id: 'map-toolbar-zoom-out-btn',
    },
    {
      icon: 'fullscreen',
      label: 'Reset View',
      id: 'map-toolbar-reset-view-btn',
    },
    {
      icon: 'gps_fixed',
      label: 'Auto Focus',
      id: 'map-toolbar-focus-btn',
    },
  ];

  toolbarButtons = computed(() => {
    let buttons = this.getBaseToolbarButtons();
    this.applyFilterActiveState(buttons);
    buttons = this.filterHiddenButtons(buttons);
    buttons = this.prependInfoButton(buttons);
    return this.prependToolbarToggleButtons(buttons);
  });

  private getBaseToolbarButtons(): ToolbarButton[] {
    if (!this.filtersToDisplay() || this.filtersToDisplay().length === 0) {
      return this._toolbarButtons.filter((button) => button.icon !== 'filter_alt');
    }
    return this._toolbarButtons;
  }

  private applyFilterActiveState(buttons: ToolbarButton[]): void {
    const filterButton = buttons.find((button) => button.icon === 'filter_alt');
    if (filterButton) {
      filterButton.isFilterActive = this.isFilterActive();
    }
  }

  private filterHiddenButtons(buttons: ToolbarButton[]): ToolbarButton[] {
    let result = buttons;
    if (this.hideSettingsBtn()) {
      result = result.filter((button) => button.icon !== 'settings');
    }
    if (this.hideAutoFocus()) {
      result = result.filter((button) => button.icon !== 'gps_fixed');
    }
    return result;
  }

  private prependInfoButton(buttons: ToolbarButton[]): ToolbarButton[] {
    if (!this.infoText()) {
      return buttons;
    }
    return [
      {
        icon: 'info',
        label: 'Information',
        id: 'map-toolbar-info-btn',
      },
      ...buttons,
    ];
  }

  private prependToolbarToggleButtons(buttons: ToolbarButton[]): ToolbarButton[] {
    let result = buttons;
    if (this.showEval()) {
      result = this.prependEvalButton(result);
    }
    if (this.showGpsPositions()) {
      result = this.prependGpsPositionsButton(result);
    }
    if (this.showDeadReckoningPath()) {
      result = this.prependDeadReckoningButton(result);
    }
    if (this.showSpeedHeatmap()) {
      result = this.prependSpeedHeatmapButton(result);
    }
    if (this.showCompareCollars()) {
      result = this.prependCompareCollarsButton(result);
    }
    return result;
  }

  private prependEvalButton(buttons: ToolbarButton[]): ToolbarButton[] {
    const evalIndex = this.infoText() ? 1 : 0;
    return [
      ...buttons.slice(0, evalIndex),
      {
        icon: 'eval',
        label: 'Skip every other position',
        id: 'map-toolbar-eval-btn',
        disabled: this.evalDisabled(),
      },
      ...buttons.slice(evalIndex),
    ];
  }

  private prependGpsPositionsButton(buttons: ToolbarButton[]): ToolbarButton[] {
    const gpsIndex = (this.infoText() ? 1 : 0) + (this.showEval() ? 1 : 0);
    return [
      ...buttons.slice(0, gpsIndex),
      {
        icon: 'location_on',
        label: 'Toggle GPS Positions',
        id: 'map-toolbar-gps-positions-btn',
      },
      ...buttons.slice(gpsIndex),
    ];
  }

  private prependDeadReckoningButton(buttons: ToolbarButton[]): ToolbarButton[] {
    const deadReckoningIndex =
      (this.infoText() ? 1 : 0) + (this.showEval() ? 1 : 0) + (this.showGpsPositions() ? 1 : 0);
    return [
      ...buttons.slice(0, deadReckoningIndex),
      {
        icon: 'route',
        label: 'Toggle Dead Reckoning Path',
        id: 'map-toolbar-dead-reckoning-path-btn',
      },
      ...buttons.slice(deadReckoningIndex),
    ];
  }

  private prependSpeedHeatmapButton(buttons: ToolbarButton[]): ToolbarButton[] {
    const speedIndex =
      (this.infoText() ? 1 : 0) +
      (this.showEval() ? 1 : 0) +
      (this.showGpsPositions() ? 1 : 0) +
      (this.showDeadReckoningPath() ? 1 : 0);
    return [
      ...buttons.slice(0, speedIndex),
      {
        icon: 'speed',
        label: 'Toggle Speed Heatmap',
        id: 'map-toolbar-speed-heatmap-btn',
      },
      ...buttons.slice(speedIndex),
    ];
  }

  private prependCompareCollarsButton(buttons: ToolbarButton[]): ToolbarButton[] {
    const compareIndex =
      (this.infoText() ? 1 : 0) +
      (this.showEval() ? 1 : 0) +
      (this.showGpsPositions() ? 1 : 0) +
      (this.showDeadReckoningPath() ? 1 : 0) +
      (this.showSpeedHeatmap() ? 1 : 0);
    return [
      ...buttons.slice(0, compareIndex),
      {
        icon: 'pets',
        label: 'Nearby Animals',
        id: 'map-toolbar-compare-btn',
      },
      ...buttons.slice(compareIndex),
    ];
  }

  // Track which panel is open
  openPanelIndex: number | null = null;
  enableDistanceMeasurement = false;

  isButtonActive(idx: number, btn: ToolbarButton) {
    if (btn.icon === 'gps_fixed') return this.autoFocusEnabledState();
    if (btn.icon === 'eval') return this.evalActive();
    if (btn.icon === 'location_on') return this.isGpsPositionsActive();
    if (btn.icon === 'route') return this.deadReckoningPathActive();
    if (btn.icon === 'speed') return this.speedHeatmapActive();
    if (btn.icon === 'pets') {
      return this.selectedCompareKeys().length > 0 || this.openPanelIndex === idx;
    }
    return this.openPanelIndex === idx;
  }

  onButtonClick(idx: number, btn: ToolbarButton) {
    if (btn.panelComponent || btn.icon === 'info' || btn.icon === 'pets') {
      // The details panel overlays the layers panel; it must never outlive it.
      this.featureLayerDetails.close();
      this.openPanelIndex = this.openPanelIndex === idx ? null : idx;
    } else {
      this.handleActionButton(idx, btn);
    }
  }

  private handleActionButton(idx: number, btn: ToolbarButton): void {
    this.openPanelIndex = null;
    if (btn.icon === 'eval') {
      this.handleEvalButton();
    } else if (btn.icon === 'location_on') {
      this.handleLocationOnButton();
    } else if (btn.icon === 'route') {
      this.deadReckoningPathToggled.emit(!this.deadReckoningPathActive());
    } else if (btn.icon === 'speed') {
      this.speedHeatmapToggled.emit(!this.speedHeatmapActive());
    } else if (btn.icon === 'zoom_in') {
      this.mapService.map?.zoomIn();
    } else if (btn.icon === 'zoom_out') {
      this.mapService.map?.zoomOut();
    } else if (btn.icon === 'fullscreen') {
      this.mapService.resetZoomToPositions();
    } else if (btn.icon === 'gps_fixed') {
      this.autoFocusEnabledState.update((value) => !value);
      this.autoFocusToggled.emit(this.autoFocusEnabledState());
    } else if (btn.icon === 'design_services') {
      this.enableDistanceMeasurement = !this.enableDistanceMeasurement;
      this.openPanelIndex = this.enableDistanceMeasurement ? idx : null;
      this.mapDistanceMeasurementService.distanceMeasurement(this.enableDistanceMeasurement);
    } else if (btn.icon === 'settings') {
      this.navigateToPreferences();
    }
  }

  private handleEvalButton(): void {
    const nextEvalState = !this.evalActive();
    this.evalToggled.emit(nextEvalState);
    if (nextEvalState && !this.isGpsPositionsActive()) {
      this.mapService.toggleGpsLayers(true);
      this.gpsPositionsToggled.emit(true);
    }
  }

  private handleLocationOnButton(): void {
    const nextState = !this.isGpsPositionsActive();
    this.mapService.toggleGpsLayers(nextState);
    this.gpsPositionsToggled.emit(nextState);
    if (!nextState && this.evalActive()) {
      this.evalToggled.emit(false);
    }
  }

  onFilterValuesSaved(event: EventRequestDto) {
    this.filterValuesSaved.emit(event);
    this.isFilterActive.set(!!(event.lastX || event.beforeUTC || event.afterUTC));
  }

  closePanel() {
    this.featureLayerDetails.close();
    this.openPanelIndex = null;
  }

  onLayersCardClosed() {
    // In details view the header arrow only steps back to the layers list;
    // the card itself stays open.
    if (this.featureLayerDetails.selectedKey()) {
      this.featureLayerDetails.close();
      return;
    }
    this.closePanel();
  }

  onInfoClick(event: Event) {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return;
    }
    this.closePanel();
  }

  navigateToPreferences() {
    this.dialogService.open(ConfirmationDialogComponent, {
      data: {
        title: 'Open Map Settings?',
        message: 'Do you want to leave this page and navigate to map settings?',
      },
      afterConfirm: () => {
        this.router.navigate(['/preferences'], { queryParams: { wasLinkClicked: true } });
      },
    });
  }

  virtualFenceFilterToggle(isToggled: boolean) {
    this.virtualFenceFilterToggled.set(isToggled);
  }
}
